import {
  aggregateMonthlyIncome,
  aggregateWeeklyIncomeForMonth,
  fetchTaskHistoryRange,
  getComparablePeriodEnd,
  getMonthDateRange,
  getTopMonth,
  getYearDateRange,
  sumIncomeUpToDate,
  WeeklyIncomeData,
} from '@/lib/payrollAnalytics';
import { useQuery } from '@tanstack/react-query';
import { TrendingDown, TrendingUp } from 'lucide-react';
import {
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis,
} from 'recharts';

interface RevenueSectionProps {
  selectedYear: number;
  selectedMonth: number;
}

interface MonthlyData {
  month: string;
  income: number;
  lastYearIncome: number;
}

interface YearlyRevenueSummary {
  monthlyData: MonthlyData[];
  totalRevenue: number;
  lastYearComparableTotal: number;
  topMonth: { month: string; income: number };
}

interface CustomLineTooltipProps extends Omit<TooltipProps<number, string>, 'payload'> {
  payload?: Array<{
    payload?: {
      month: string;
    };
    value?: number;
  }>;
}

const COLORS = ['#4318FF', '#6AD2FF', '#EFF4FB', '#E9EDF7'];

const formatCurrency = (value: number | undefined): string => {
  if (!value) return '$0';
  return `$${value.toLocaleString()}`;
};

const CustomLineTooltip: React.FC<CustomLineTooltipProps> = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-2 border border-gray-200 rounded shadow-sm">
        <p className="text-sm font-medium">{payload[0]?.payload?.month}</p>
        <div className="text-sm text-gray-600">
          <p>This Year: ${payload[0]?.value?.toLocaleString()}</p>
          {payload[1] && <p>Last Year: ${payload[1]?.value?.toLocaleString()}</p>}
        </div>
      </div>
    );
  }
  return null;
};

const RevenueSection: React.FC<RevenueSectionProps> = ({ selectedYear, selectedMonth }) => {
  const {
    data: yearlySummary,
    isLoading: yearlyLoading,
    isError: yearlyError,
  } = useQuery({
    queryKey: ['revenue', 'yearly', selectedYear],
    queryFn: async (): Promise<YearlyRevenueSummary> => {
      const currentYearRange = getYearDateRange(selectedYear);
      const lastYearRange = getYearDateRange(selectedYear - 1);
      const comparableEnd = getComparablePeriodEnd(selectedYear);
      const lastYearComparableEnd = comparableEnd.replace(
        String(selectedYear),
        String(selectedYear - 1)
      );

      const [currentYearData, lastYearData] = await Promise.all([
        fetchTaskHistoryRange(currentYearRange.start, currentYearRange.end),
        fetchTaskHistoryRange(lastYearRange.start, lastYearRange.end),
      ]);

      const currentYearTotals = aggregateMonthlyIncome(currentYearData, selectedYear);
      const lastYearTotals = aggregateMonthlyIncome(lastYearData, selectedYear - 1);

      const monthlyData: MonthlyData[] = Array.from({ length: 12 }, (_, month) => ({
        month: new Date(2000, month).toLocaleString('default', { month: 'short' }),
        income: currentYearTotals[month] || 0,
        lastYearIncome: lastYearTotals[month] || 0,
      }));

      return {
        monthlyData,
        totalRevenue: sumIncomeUpToDate(currentYearData, selectedYear, comparableEnd),
        lastYearComparableTotal: sumIncomeUpToDate(
          lastYearData,
          selectedYear - 1,
          lastYearComparableEnd
        ),
        topMonth: getTopMonth(currentYearTotals, selectedYear),
      };
    },
  });

  const {
    data: monthlyData,
    isLoading: monthlyLoading,
    isError: monthlyError,
  } = useQuery({
    queryKey: ['revenue', 'monthly', selectedYear, selectedMonth],
    queryFn: async (): Promise<WeeklyIncomeData[]> => {
      const { start, end } = getMonthDateRange(selectedYear, selectedMonth);
      const rows = await fetchTaskHistoryRange(start, end);
      return aggregateWeeklyIncomeForMonth(rows, selectedYear, selectedMonth);
    },
  });

  const yearlyData = yearlySummary?.monthlyData;
  const totalRevenue = yearlySummary?.totalRevenue ?? 0;
  const lastYearComparableTotal = yearlySummary?.lastYearComparableTotal ?? 0;
  const percentageChange = lastYearComparableTotal
    ? ((totalRevenue - lastYearComparableTotal) / lastYearComparableTotal) * 100
    : 0;
  const topMonth = yearlySummary?.topMonth;
  const topWeek = monthlyData?.[0];
  const monthlyTotal = monthlyData?.reduce((sum, week) => sum + week.total, 0) ?? 0;
  const isCurrentYear = selectedYear === new Date().getFullYear();

  if (yearlyLoading || monthlyLoading) {
    return <div>Loading...</div>;
  }

  if (yearlyError || monthlyError) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
        Unable to load revenue analytics. Please refresh or try again.
      </div>
    );
  }

  return (
    <div className="space-y-1 sm:space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-1 sm:gap-3">
        <div className="bg-white p-6 rounded-lg shadow-sm">
          <h3 className="text-sm">Total Revenue</h3>
          <div className="text-3xl font-bold mt-2 mb-2">${totalRevenue.toLocaleString()}</div>
          <div
            className={`flex items-center text-sm ${percentageChange >= 0 ? 'text-green-500' : 'text-red-500'}`}
          >
            {percentageChange >= 0 ? (
              <TrendingUp className="w-4 h-4 mr-1" />
            ) : (
              <TrendingDown className="w-4 h-4 mr-1" />
            )}
            {Math.abs(percentageChange).toFixed(1)}% compare to last year
          </div>
          {isCurrentYear && (
            <p className="mt-1 text-xs text-gray-500">Year-to-date through today</p>
          )}
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm">
          <h3 className="text-sm">Top Month</h3>
          <div className="text-3xl font-bold mt-2 mb-2">{topMonth?.month || '-'}</div>
          <div className="text-2xl font-bold text-gray-900">
            ${topMonth?.income?.toLocaleString() ?? '0'}
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm">
          <h3 className="text-sm">Top Week</h3>
          <div className="text-3xl font-bold mt-2 mb-2">{topWeek?.weekRange || '-'}</div>
          <div className="text-2xl font-bold text-gray-900">
            ${topWeek?.total?.toLocaleString() ?? '0'}
          </div>
          <p className="mt-1 text-xs text-gray-500">
            Best week in{' '}
            {new Date(selectedYear, selectedMonth).toLocaleString('default', { month: 'long' })}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-1 sm:gap-3">
        <div className=" col-span-1 sm:col-span-2 bg-white p-6 rounded-lg shadow-sm">
          <h3 className="text-lg font-semibold mb-4">Monthly Revenue</h3>
          <div className="h-[180px] sm:h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={yearlyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" tick={{ fontSize: 12 }} stroke="#718096" />
                <YAxis tickFormatter={formatCurrency} tick={{ fontSize: 12 }} stroke="#718096" />
                <Tooltip content={<CustomLineTooltip />} />
                <Legend wrapperStyle={{ fontSize: '12px' }} verticalAlign="top" height={36} />
                <Line
                  type="monotone"
                  dataKey="income"
                  stroke="hsl(var(--chart-1))"
                  strokeWidth={3}
                  dot={true}
                  name="This Year"
                />
                <Line
                  type="monotone"
                  dataKey="lastYearIncome"
                  stroke="hsl(var(--chart-2))"
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  dot={false}
                  name="Last Year"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm">
          <h3 className="text-lg font-semibold mb-4">Weekly Distribution</h3>
          <div className="relative h-[180px]">
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center">
                <div className="text-gray-500 text-sm mb-1">
                  {new Date(selectedYear, selectedMonth).toLocaleString('default', {
                    month: 'long',
                  })}
                </div>
                <div className="text-2xl font-bold">${monthlyTotal.toLocaleString()}</div>
              </div>
            </div>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={monthlyData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={80}
                  fill="#8884d8"
                  paddingAngle={2}
                  dataKey="total"
                >
                  {monthlyData?.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      const data = payload[0].payload;
                      return (
                        <div className="custom-tooltip bg-white p-2 rounded-lg shadow-lg text-sm">
                          {`${data.weekRange} : ${formatCurrency(data.total)}`}
                        </div>
                      );
                    }
                    return null;
                  }}
                  contentStyle={{
                    backgroundColor: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    zIndex: 1000,
                    fontSize: '12px',
                    padding: '6px 8px',
                  }}
                  wrapperStyle={{ zIndex: 1000 }}
                  itemStyle={{ fontSize: '12px', padding: '1px 0' }}
                  labelStyle={{ fontSize: '12px' }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-6">
            <div className="grid grid-cols-2 md:grid-cols-[2fr_1fr] pb-2 text-gray-500 text-sm">
              <span>Week Range</span>
              <span className="text-right">Revenue</span>
            </div>

            <div className="mt-2">
              {monthlyData?.map((item, index) => (
                <div
                  key={item.weekRange}
                  className="grid grid-cols-2 md:grid-cols-[2fr_1fr] items-center py-1.5 text-sm text-gray-700 border-b border-[#EDF2F7] last:border-b-0"
                >
                  <div className="flex items-center">
                    <div
                      className="w-2 h-2 rounded-full mr-2"
                      style={{ backgroundColor: COLORS[index % COLORS.length] }}
                    />
                    {item.weekRange}
                  </div>
                  <div className="text-right text-gray-600">${item.total.toLocaleString()}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RevenueSection;
